import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const BrandContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DEFAULT_BRAND = {
  brand_name: 'Care Tech Compare',
  logo_url: '/brand/ctc-favicon-64.png',
  accent_color: '#00A79D',
  sender_name: 'Care Tech Compare',
};

// --- colour helpers -------------------------------------------------------
const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return { r: 0, g: 174, b: 239 };
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const rgbToHex = ({ r, g, b }) =>
  '#' + [r, g, b].map((x) => clamp(x).toString(16).padStart(2, '0')).join('');
const mix = (hex, target, amt) => {
  const c = hexToRgb(hex);
  return rgbToHex({
    r: c.r + (target - c.r) * amt,
    g: c.g + (target - c.g) * amt,
    b: c.b + (target - c.b) * amt,
  });
};
const lighten = (hex, amt) => mix(hex, 255, amt);
const darken = (hex, amt) => mix(hex, 0, amt);
const hexToHsl = (hex) => {
  let { r, g, b } = hexToRgb(hex);
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

/** Apply the chosen accent globally: shadcn --primary/--ring + a runtime
 *  override of the solid violet-* accent utilities used across the app. */
export const applyAccent = (accent) => {
  const root = document.documentElement;
  const base = accent || DEFAULT_BRAND.accent_color;
  const light = lighten(base, 0.14);
  const dark = darken(base, 0.16);
  root.style.setProperty('--primary', hexToHsl(base));
  root.style.setProperty('--ring', hexToHsl(base));
  root.style.setProperty('--brand', base);

  let el = document.getElementById('brand-accent-overrides');
  if (!el) {
    el = document.createElement('style');
    el.id = 'brand-accent-overrides';
    document.head.appendChild(el);
  }
  el.textContent = `
    .bg-violet-600{background-color:${base} !important}
    .bg-violet-500{background-color:${light} !important}
    .bg-violet-700{background-color:${dark} !important}
    .hover\\:bg-violet-500:hover{background-color:${light} !important}
    .hover\\:bg-violet-700:hover{background-color:${dark} !important}
    .text-violet-600,.text-violet-500,.text-violet-400,.text-violet-300{color:${base} !important}
    .border-violet-600,.border-violet-500,.border-violet-700{border-color:${base} !important}
    .ring-violet-500,.ring-violet-600{--tw-ring-color:${base} !important}
  `;
};

export const BrandProvider = ({ children }) => {
  const { token } = useAuth();
  const [brand, setBrand] = useState(DEFAULT_BRAND);

  const refreshBrand = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/brand-settings`);
      const b = { ...DEFAULT_BRAND, ...res.data };
      setBrand(b);
      applyAccent(b.accent_color);
      return b;
    } catch {
      applyAccent(DEFAULT_BRAND.accent_color);
      return DEFAULT_BRAND;
    }
  }, []);

  useEffect(() => {
    if (token) refreshBrand();
    else applyAccent(DEFAULT_BRAND.accent_color);
  }, [token, refreshBrand]);

  return (
    <BrandContext.Provider value={{ brand, setBrand, refreshBrand }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within a BrandProvider');
  return ctx;
};
